import React, { useState, useRef, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { Calendar, MapPin, Search, Star, DollarSign, Heart, TrendingUp, User, ChevronDown, LogOut, Clock, CheckCircle, XCircle, Filter, MapPinIcon, Users, X, ChevronLeft, ChevronRight, CreditCard, Check, Info, Map as MapIcon, List, PawPrint } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './components/ui/dropdown-menu';
import { ImageWithFallback } from './components/figma/ImageWithFallback';
import { Separator } from './components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './components/ui/dialog';
import { RadioGroup, RadioGroupItem } from './components/ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from './components/ui/carousel';
import { Progress } from './components/ui/progress';
import { DiscgoLogo } from './components/DiscgoLogo';
import basketIcon from 'figma:asset/f4b6087bb5a2b853ba4fdd33278c203eb98d743e.png';
import BookingsPage from './pages/Bookings';
import PaymentPage from './pages/Payment';
import AccountPage from './pages/Account';
import Header from './components/Header';
import MapView from './components/MapView';
import { fetchCourses, fetchCourseById, type Course } from './services/courseService';
import { fetchUserBookings, type BookingData } from './services/bookingsListService';
import { generateTeeTimesForCourse, type GeneratedTeeTimeSlot } from './services/teeTimeGeneratorService';
import { createBooking, cancelBooking, type CreateBookingError } from './services/bookingService';
import { useAuth } from './contexts/AuthContext';
import { supabase } from './lib/supabase';
import { apiPost, apiGet } from './lib/api';
import { StripeCheckout, type SavedCardInfo } from './components/StripeCheckout';
const DISCGO_SITE_URL = import.meta.env.VITE_DISCGO_SITE_URL || 'http://localhost:3000';
const WAITLIST_MODE = import.meta.env.VITE_WAITLIST_MODE === 'true';
import { toast } from 'sonner';
import { Toaster } from './components/ui/sonner';

// Brand colors matching the course manager dashboard
const brandStyles = {
  primary: 'bg-[#2B8B3B] text-white hover:bg-[#236B2F]',
  secondary: 'bg-[#F5F5DC] text-[#2B8B3B] hover:bg-[#f0ebe1] hover:text-[#2B8B3B] border-[#2B8B3B]',
  accent: 'bg-[#F5F5DC]',
  text: 'text-[#2B8B3B]'
};

// All mock data removed - now using real data from Supabase

type PlayerPage = 'dashboard' | 'bookings' | 'payment' | 'account';
const PLAYER_PAGE_TO_PATH: Record<PlayerPage, string> = {
  dashboard: '/',
  bookings: '/bookings',
  payment: '/payment',
  account: '/account',
};
const PLAYER_PATH_TO_PAGE: Record<string, PlayerPage> = {
  '/': 'dashboard',
  '/bookings': 'bookings',
  '/payment': 'payment',
  '/account': 'account',
};
function playerPageFromPath(pathname: string): PlayerPage {
  const seg = '/' + pathname.replace(/^\//, '').split('/')[0];
  return PLAYER_PATH_TO_PAGE[seg] || 'dashboard';
}

function WaitlistHolding({ state }: { state: string | null }) {
  const { signOut } = useAuth();
  return (
    <div className="min-h-screen bg-[#F5F5DC] flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center space-y-4">
        <DiscgoLogo style={{ height: '60px', width: '60px', display: 'block', margin: '0 auto' }} />
        <h1 className="text-xl font-bold text-[#2B8B3B] lowercase">you're on the waitlist</h1>
        <p className="text-gray-600 text-sm lowercase">
          {state
            ? <>we'll email you as soon as discgo launches in <strong>{state}</strong>.</>
            : <>we'll email you as soon as discgo launches in your area.</>
          }
        </p>
        <button
          onClick={signOut}
          className="mt-4 text-sm text-gray-500 underline hover:text-gray-700 lowercase"
        >
          sign out
        </button>
      </div>
    </div>
  );
}

export default function App() {
  const { user, loading: authLoading, waitlistStatus, playerState } = useAuth();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [upcomingBookings, setUpcomingBookings] = useState<BookingData[]>([]);
  const [pastBookings, setPastBookings] = useState<BookingData[]>([]);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState<BookingData | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [favoriteCourses, setFavoriteCourses] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('discgo_favorites');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [userLocation, setUserLocation] = useState<{ lat: number; lon: number } | null>(null);
  const [courseImageIndexes, setCourseImageIndexes] = useState<Record<string, number>>({});
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);
  const [currentPage, setCurrentPage] = useState<PlayerPage>(() => playerPageFromPath(window.location.pathname));
  const [scrollToDistroFund, setScrollToDistroFund] = useState(false);
  
  // Refs for scrolling
  const bookingsRef = useRef<HTMLDivElement>(null);
  const coursesRef = useRef<HTMLDivElement>(null);
  
  // Booking flow state
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [bookingStep, setBookingStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedConfigId, setSelectedConfigId] = useState('');
  const [selectedSlotStart, setSelectedSlotStart] = useState('');
  const [selectedPriceCents, setSelectedPriceCents] = useState(0);
  const [selectedBookingType, setSelectedBookingType] = useState<'auto' | 'request'>('auto');
  const [availableTeeTimes, setAvailableTeeTimes] = useState<GeneratedTeeTimeSlot[]>([]);
  const [loadingTeeTimes, setLoadingTeeTimes] = useState(false);
  const [playerCount, setPlayerCount] = useState(4);
  const [playerName, setPlayerName] = useState('');
  const [playerEmail, setPlayerEmail] = useState('');
  const [playerPhone, setPlayerPhone] = useState('');
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [confirmationNumber, setConfirmationNumber] = useState('');
  const [policyAgreed, setPolicyAgreed] = useState(false);

  // Stripe payment state
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentAmounts, setPaymentAmounts] = useState<{
    courseAmountCents: number;
    bookingFeeCents: number;
    stripeFeeCents: number;
    totalCents: number;
    refundPolicyName: string;
  } | null>(null);
  const [paymentIntentLoading, setPaymentIntentLoading] = useState(false);
  const [paymentIntentError, setPaymentIntentError] = useState<string | null>(null);
  const [savedCard, setSavedCard] = useState<SavedCardInfo | null>(null);
  const [stripeCustomerId, setStripeCustomerId] = useState<string | null>(null);
  const [saveCard, setSaveCard] = useState(false);
  const [usingSavedCard, setUsingSavedCard] = useState(false);

  // Holds display data after Stripe redirects back, since React state is cleared on navigation
  const [confirmedBookingDisplay, setConfirmedBookingDisplay] = useState<{
    courseName: string;
    dateStr: string;
    timeStr: string;
    playerCount: number;
    playerEmail: string;
    totalPaid: string;
    confirmationNumber: string;
    refundPolicyName: string;
    isRequest?: boolean;
  } | null>(null);

  // Fetch courses once user session is established
  useEffect(() => {
    if (!user) return;
    async function loadCourses() {
      setLoadingCourses(true);
      const data = await fetchCourses();
      setCourses(data);
      setLoadingCourses(false);
    }
    loadCourses();
  }, [user]);

  // Pre-populate booking form with signed-in user's profile data
  useEffect(() => {
    if (!user) return;
    supabase
      .from('profiles')
      .select('first_name, last_name, phone')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        const name = [data?.first_name, data?.last_name].filter(Boolean).join(' ');
        setPlayerName(name || '');
        setPlayerEmail(user.email ?? '');
        setPlayerPhone(data?.phone ?? '');
      });
  }, [user]);

  // Function to load bookings
  const loadBookings = async () => {
    if (!user?.id) return;
    
    setLoadingBookings(true);
    const { upcoming, past } = await fetchUserBookings(user.id);
    setUpcomingBookings(upcoming);
    setPastBookings(past);
    setLoadingBookings(false);
  };

  // Fetch bookings from Supabase on mount
  useEffect(() => {
    loadBookings();
  }, [user]);

  // Persist favorites to localStorage
  useEffect(() => {
    localStorage.setItem('discgo_favorites', JSON.stringify(favoriteCourses));
  }, [favoriteCourses]);

  // Request user geolocation for distance calculation
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setUserLocation({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      () => {} // silently ignore denied/unavailable
    );
  }, []);

  // Handle booking deep link: ?bookCourse=:courseId
  const bookCourseHandled = useRef(false);
  useEffect(() => {
    if (bookCourseHandled.current || authLoading || !user) return;
    const params = new URLSearchParams(window.location.search);
    const bookCourseId = params.get('bookCourse');
    if (!bookCourseId) return;
    bookCourseHandled.current = true;
    // Clean the param from the URL
    window.history.replaceState({}, '', window.location.pathname);
    fetchCourseById(bookCourseId).then(course => {
      if (course) openBookingFlow(course);
    });
  }, [user, authLoading]);

  // Handle Stripe post-payment redirect: ?payment_intent=pi_xxx&redirect_status=succeeded
  const postPaymentHandled = useRef(false);
  useEffect(() => {
    if (postPaymentHandled.current || authLoading || !user) return;
    const params = new URLSearchParams(window.location.search);
    const paymentIntentId = params.get('payment_intent');
    const redirectStatus = params.get('redirect_status');
    if (!paymentIntentId || redirectStatus !== 'succeeded') return;
    postPaymentHandled.current = true;

    window.history.replaceState({}, '', window.location.pathname);

    const pending = JSON.parse(sessionStorage.getItem('discgo_stripe_pending') || 'null');
    sessionStorage.removeItem('discgo_stripe_pending');

    apiPost<{ booking: any }>('/api/payments/confirm', { paymentIntentId })
      .then(({ booking }) => {
        setConfirmedBookingDisplay({
          courseName: pending?.courseName || 'Course',
          dateStr: pending?.selectedDate || '',
          timeStr: pending?.selectedTime || '',
          playerCount: booking.qty,
          playerEmail: pending?.playerEmail || '',
          totalPaid: `$${Number(booking.total_dollar_amount).toFixed(2)}`,
          confirmationNumber: booking.confirmation_number || booking.id.slice(0, 5).toUpperCase(),
          refundPolicyName: booking.refund_policy_name || 'Flexible',
          isRequest: booking.status === 'requested',
        });
        setSelectedBookingType('auto');
        setBookingConfirmed(true);
        setBookingDialogOpen(true);
        loadBookings();
      })
      .catch((err) => {
        console.error('Post-payment confirmation failed:', err);
        toast.error('Payment received but booking creation failed. Please contact support.');
      });
  }, [user, authLoading]);

  // Helper: (re-)create a PaymentIntent with the current saved-card preferences
  const createPaymentIntent = async (opts: {
    cid: string | null;
    useSaved: boolean;
    doSave: boolean;
  }) => {
    if (!selectedCourse || !selectedConfigId || !selectedSlotStart || !playerCount) return;
    setPaymentIntentLoading(true);
    setPaymentIntentError(null);
    setClientSecret(null);

    try {
      const data = await apiPost<{
        clientSecret: string;
        courseAmountCents: number;
        bookingFeeCents: number;
        stripeFeeCents: number;
        totalCents: number;
        refundPolicyName: string;
        refundHoursBeforeTeeTime: number;
      }>('/api/payments/create-payment-intent', {
        teeTimeConfigId: selectedConfigId,
        courseId: selectedCourse.id,
        slotStart: selectedSlotStart,
        playerCount,
        customerId: opts.cid ?? undefined,
        useSavedCard: opts.useSaved,
        saveCard: opts.doSave,
        bookingType: selectedBookingType,
      });
      setClientSecret(data.clientSecret);
      setPaymentAmounts({
        courseAmountCents: data.courseAmountCents,
        bookingFeeCents: data.bookingFeeCents,
        stripeFeeCents: data.stripeFeeCents,
        totalCents: data.totalCents,
        refundPolicyName: data.refundPolicyName,
      });
    } catch {
      setPaymentIntentError('Unable to set up payment. Please try again.');
    } finally {
      setPaymentIntentLoading(false);
    }
  };

  // Create Stripe PaymentIntent when player reaches checkout step (auto and request bookings)
  useEffect(() => {
    if (bookingStep !== 3) return;
    if (!user) return;
    if (!selectedCourse || !selectedConfigId || !selectedSlotStart || !playerCount) return;

    // Reset saved-card state for this booking session
    setSavedCard(null);
    setStripeCustomerId(null);
    setSaveCard(false);
    setUsingSavedCard(false);

    // Persist display info so we can rebuild the confirmation screen after Stripe redirect
    sessionStorage.setItem('discgo_stripe_pending', JSON.stringify({
      courseName: selectedCourse.name,
      selectedDate,
      selectedTime,
      playerCount,
      playerEmail,
      bookingType: selectedBookingType,
    }));

    setPaymentIntentLoading(true);
    setPaymentIntentError(null);
    setClientSecret(null);

    (async () => {
      // 1. Fetch saved card (fall back silently on error)
      let card: SavedCardInfo | null = null;
      try {
        const result = await apiGet<{ savedCard: SavedCardInfo | null }>('/api/payments/saved-card');
        card = result.savedCard;
      } catch { /* non-fatal */ }
      setSavedCard(card);

      // 2. Get or create Stripe Customer (fall back silently on error)
      let cid: string | null = null;
      try {
        const result = await apiPost<{ customerId: string }>('/api/payments/get-or-create-customer', {});
        cid = result.customerId;
      } catch { /* non-fatal */ }
      setStripeCustomerId(cid);

      const useSaved = !!card && !!cid;
      setUsingSavedCard(useSaved);

      // 3. Create PaymentIntent
      await createPaymentIntent({ cid, useSaved, doSave: false });
    })();
  }, [bookingStep, selectedBookingType, user]);

  // Fetch tee times when course or date changes
  useEffect(() => {
    async function loadTeeTimes() {
      if (!selectedCourse || !selectedDate) {
        setAvailableTeeTimes([]);
        return;
      }

      setLoadingTeeTimes(true);
      // Pass the date string directly (YYYY-MM-DD format) to preserve the date in the course's timezone
      const teeTimes = await generateTeeTimesForCourse(selectedCourse.id, selectedDate);
      setAvailableTeeTimes(teeTimes);
      setLoadingTeeTimes(false);
    }
    loadTeeTimes();
  }, [selectedCourse, selectedDate]);

  const scrollToBookings = () => {
    bookingsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const toggleFavoritesFilter = () => {
    setShowOnlyFavorites(!showOnlyFavorites);
    coursesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleNavigate = (page: PlayerPage, scrollToDistro = false) => {
    const path = PLAYER_PAGE_TO_PATH[page] || '/';
    window.history.pushState({ page }, '', path);
    setCurrentPage(page);
    setScrollToDistroFund(scrollToDistro);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const onPopState = () => setCurrentPage(playerPageFromPath(window.location.pathname));
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const toggleFilter = (filter: string) => {
    setActiveFilters(prev => 
      prev.includes(filter) 
        ? prev.filter(f => f !== filter)
        : [...prev, filter]
    );
  };

  const toggleFavorite = (courseId: string) => {
    setFavoriteCourses(prev => 
      prev.includes(courseId)
        ? prev.filter(id => id !== courseId)
        : [...prev, courseId]
    );
  };

  const navigateCourseImage = (courseId: string, direction: 'prev' | 'next', imageCount: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setCourseImageIndexes(prev => {
      const currentIndex = prev[courseId] || 0;
      let newIndex;
      if (direction === 'next') {
        newIndex = (currentIndex + 1) % imageCount;
      } else {
        newIndex = currentIndex === 0 ? imageCount - 1 : currentIndex - 1;
      }
      return { ...prev, [courseId]: newIndex };
    });
  };

  const openBookingFlow = (course: Course) => {
    setSelectedCourse(course);
    setBookingStep(1);
    setBookingDialogOpen(true);
    setBookingConfirmed(false);
    // Set default date to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setSelectedDate(tomorrow.toISOString().split('T')[0]);
  };

  const handleNextStep = async () => {
    if (bookingStep < 3) {
      setBookingStep(bookingStep + 1);
      return;
    }

    // Step 3: StripeCheckout handles submission for both auto and request bookings
  };

  const handlePreviousStep = () => {
    if (bookingStep > 1) {
      setBookingStep(bookingStep - 1);
    }
  };

  const handleSaveCardChange = async (val: boolean) => {
    setSaveCard(val);
    if (!clientSecret) return;
    // Update setup_future_usage on the existing PI in-place so <Elements> doesn't remount
    const paymentIntentId = clientSecret.split('_secret_')[0];
    try {
      await apiPost('/api/payments/update-payment-intent', { paymentIntentId, saveCard: val });
    } catch {
      // Non-fatal — payment can still proceed; card just won't be saved
    }
  };

  const handleUseDifferentCard = async () => {
    setUsingSavedCard(false);
    await createPaymentIntent({ cid: stripeCustomerId, useSaved: false, doSave: saveCard });
  };

  const closeBookingDialog = () => {
    setBookingDialogOpen(false);
    setBookingStep(1);
    setSelectedCourse(null);
    setSelectedTime('');
    setSelectedConfigId('');
    setSelectedSlotStart('');
    setSelectedPriceCents(0);
    setAvailableTeeTimes([]);
    setPolicyAgreed(false);
    setClientSecret(null);
    setPaymentAmounts(null);
    setPaymentIntentError(null);
    setConfirmedBookingDisplay(null);
    setBookingConfirmed(false);
  };

  const isRefundEligible = (booking: BookingData): boolean => {
    const now = new Date();
    const teeTime = new Date(booking.slotStart);
    if (now >= teeTime) return false;
    if (booking.refundHoursSnapshot === null) return false;
    if (booking.refundHoursSnapshot === 0) return true;
    const hoursUntil = (teeTime.getTime() - now.getTime()) / (1000 * 60 * 60);
    return hoursUntil >= booking.refundHoursSnapshot;
  };

  const openCancelDialog = (booking: BookingData) => {
    setBookingToCancel(booking);
    setCancelDialogOpen(true);
  };

  const confirmCancelBooking = async () => {
    if (!bookingToCancel) return;
    setCancelLoading(true);
    const success = await cancelBooking(bookingToCancel.id);
    setCancelLoading(false);
    setCancelDialogOpen(false);
    setBookingToCancel(null);
    if (success) {
      toast.success('Booking cancelled successfully');
      loadBookings();
    } else {
      toast.error('Failed to cancel booking. Please try again.');
    }
  };

  const canProceedToNextStep = () => {
    if (bookingStep === 1) {
      const selectedSlot = availableTeeTimes.find(
        s => s.configId === selectedConfigId && s.startTime === selectedSlotStart
      );
      return selectedDate && selectedTime && selectedSlot && !selectedSlot.isBooked;
    } else if (bookingStep === 2) {
      return playerCount > 0 && playerName && playerEmail;
    } else if (bookingStep === 3) {
      // StripeCheckout handles its own submit; outer button is hidden for both types
      return policyAgreed && !!clientSecret;
    }
    return false;
  };

  const calculateTotal = () => {
    if (!selectedCourse) return 0;
    return selectedCourse.pricePerPlayer * playerCount;
  };

  const calculateBookingFee = () => {
    const feePercent = (selectedCourse?.bookingFeePercent ?? 5) / 100;
    return Math.round(calculateTotal() * feePercent * 100) / 100;
  };

  const calculateTotalWithFees = () => {
    return calculateTotal() + calculateBookingFee();
  };

  const getRefundPolicyDescription = (policy: string) => {
    switch (policy) {
      case 'Flexible':
        return 'Full refund available any time before your tee time.';
      case 'Standard':
        return 'Full refund available if cancelled 24+ hours before your tee time.';
      case 'Strict':
        return 'Full refund available if cancelled 48+ hours before your tee time.';
      default:
        return '';
    }
  };

  const haversineDistanceMiles = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 3958.8; // Earth radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const getCourseDistance = (course: Course): number | null => {
    if (!userLocation) return null;
    return haversineDistanceMiles(userLocation.lat, userLocation.lon, course.lat, course.lon);
  };

  const formatDistance = (miles: number): string => {
    return miles < 10 ? `${miles.toFixed(1)} mi` : `${Math.round(miles)} mi`;
  };

  const filteredCourses = courses.filter(course => {
    // Apply favorites filter first
    if (showOnlyFavorites && !favoriteCourses.includes(course.id)) {
      return false;
    }
    
    // Use unified search term for both views
    const searchMatch = searchTerm
      ? (course.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
         course.location.toLowerCase().includes(searchTerm.toLowerCase()))
      : true;
    
    if (activeFilters.length === 0) return searchMatch;
    
    // Apply filters
    let filterMatch = true;
    
    if (activeFilters.includes('near-me')) {
      const dist = getCourseDistance(course);
      filterMatch = filterMatch && (dist !== null ? dist < 10 : false);
    }
    if (activeFilters.includes('under-20')) {
      filterMatch = filterMatch && course.pricePerPlayer < 20;
    }
    if (activeFilters.includes('high-rated')) {
      filterMatch = filterMatch && course.rating >= 4.5;
    }
    if (activeFilters.includes('available-today')) {
      filterMatch = filterMatch && course.availability === 'Available Today';
    }
    if (activeFilters.includes('dog-friendly')) {
      filterMatch = filterMatch && course.amenities.includes('Dog Friendly');
    }
    
    return searchMatch && filterMatch;
  });
  
  // Derive the display city from actual filtered courses
  const displayCity = filteredCourses.length > 0 
    ? filteredCourses[0].location 
    : 'your area';

  const CourseCard = ({ course }: { course: Course }) => {
    const currentImageIndex = courseImageIndexes[course.id] || 0;
    const distMiles = getCourseDistance(course);
    const distDisplay = distMiles !== null ? formatDistance(distMiles) : null;
    return (
      <Card className="overflow-hidden border-2 border-gray-100 hover:border-[#2B8B3B] transition-all hover:shadow-lg group">
        <div className="relative">
          <ImageWithFallback
            src={course.images[currentImageIndex]}
            alt={course.name}
            className="w-full h-48 object-cover"
          />
          
          {/* Image Navigation Arrows */}
          {course.images.length > 1 && (
            <>
              <button
                onClick={(e) => navigateCourseImage(course.id, 'prev', course.images.length, e)}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-all shadow-md opacity-0 group-hover:opacity-100"
              >
                <ChevronLeft className="w-5 h-5 text-gray-700" />
              </button>
              <button
                onClick={(e) => navigateCourseImage(course.id, 'next', course.images.length, e)}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-all shadow-md opacity-0 group-hover:opacity-100"
              >
                <ChevronRight className="w-5 h-5 text-gray-700" />
              </button>
              
              {/* Image Indicators */}
              <div className="absolute bottom-16 left-1/2 -translate-x-1/2 flex gap-1.5">
                {course.images.map((_, index) => (
                  <div
                    key={index}
                    className={`w-1.5 h-1.5 rounded-full transition-all ${
                      index === currentImageIndex 
                        ? 'bg-white w-4' 
                        : 'bg-white/60'
                    }`}
                  />
                ))}
              </div>
            </>
          )}
          
          <button
            onClick={() => toggleFavorite(course.id)}
            className="absolute top-3 right-3 w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-all shadow-md z-10"
          >
            <Heart
              className={`w-5 h-5 ${favoriteCourses.includes(course.id) ? 'fill-[#2B8B3B] text-[#2B8B3B]' : 'text-gray-600'}`}
            />
          </button>
          <Badge className={`absolute bottom-3 left-3 ${brandStyles.primary}`}>
            {course.availability}
          </Badge>
        </div>
        <CardContent className="p-4">
          <h4 className="mb-1">{course.name}</h4>
          <div className="flex items-center gap-1 text-gray-600 mb-3">
            <MapPin className="w-4 h-4" />
            <span className="text-sm">{course.location}{distDisplay ? ` · ${distDisplay}` : ''}</span>
          </div>
          
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-1">
              <Star className={`w-4 h-4 ${course.reviews > 0 ? 'fill-[#2B8B3B]' : 'fill-gray-300'} ${course.reviews > 0 ? brandStyles.text : 'text-gray-300'}`} />
              {course.reviews > 0 ? (
                <>
                  <span className="font-medium">{course.rating}</span>
                  <span className="text-sm text-gray-500">({course.reviews})</span>
                </>
              ) : (
                <span className="text-sm text-gray-400">No reviews yet</span>
              )}
            </div>
            <span className={`font-medium ${brandStyles.text}`}>${course.pricePerPlayer}/player</span>
          </div>

          <div className="flex items-center gap-4 mb-4 text-sm text-gray-600">
            <span>{course.holes} holes</span>
          </div>

          <Button 
            className={`w-full ${brandStyles.primary} rounded-lg group-hover:shadow-md transition-all`}
            onClick={() => openBookingFlow(course)}
          >
            View Details
          </Button>
        </CardContent>
      </Card>
    );
  };

  // Show loading while auth is initializing
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#F5F5DC] flex items-center justify-center">
        <div className="text-center">
          <DiscgoLogo style={{ height: '70px', width: '70px', display: 'block', margin: '0 auto 1rem' }} />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    const isExplicitSignout = sessionStorage.getItem('explicit-signout');
    sessionStorage.removeItem('explicit-signout');
    window.location.href = isExplicitSignout
      ? `${DISCGO_SITE_URL}/login?signout=1`
      : `${DISCGO_SITE_URL}/login`;
    return null;
  }

  if (WAITLIST_MODE && waitlistStatus === 'waitlisted') {
    return <WaitlistHolding state={playerState} />;
  }

  // Render different pages based on current page state
  if (currentPage === 'bookings') {
    return <BookingsPage onNavigate={handleNavigate} scrollToDistroFund={scrollToDistroFund} onBookAgain={(courseId) => {
      handleNavigate('dashboard');
      fetchCourseById(courseId).then(course => { if (course) openBookingFlow(course); });
    }} />;
  }

  if (currentPage === 'payment') {
    return <PaymentPage onNavigate={handleNavigate} />;
  }

  if (currentPage === 'account') {
    return <AccountPage onNavigate={handleNavigate} />;
  }

  return (
    <div className="min-h-screen bg-white">
      <Toaster position="top-center" richColors />
      {/* Header */}
      <Header onNavigate={handleNavigate} />

      {/* Spacer for fixed header */}
      <div style={{ height: '64px' }}></div>

      {/* Top Section - Beige Background */}
      <div className="bg-[#F5F5DC] pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8">
          {/* Welcome Section */}
          <div className="mb-8">
            <h2 className={brandStyles.text}>Welcome back, {playerName.split(' ')[0] || user?.email?.split('@')[0] || 'there'}.</h2>
            <p className="text-gray-600 mt-1">Find your next disc golf adventure</p>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 max-w-5xl">
          <Card 
            className="border-2 border-gray-100 cursor-pointer hover:border-[#2B8B3B] transition-all"
            onClick={scrollToBookings}
          >
            <CardHeader className="pb-3">
              <CardDescription>Upcoming Tee Times</CardDescription>
              <div className="flex items-baseline gap-2">
                <CardTitle className="text-3xl">{loadingBookings ? '-' : upcomingBookings.length}</CardTitle>
                <Clock className={`w-5 h-5 ${brandStyles.text}`} />
              </div>
            </CardHeader>
          </Card>

          <Card 
            className="border-2 border-gray-100 cursor-pointer hover:border-[#2B8B3B] transition-all"
            onClick={toggleFavoritesFilter}
          >
            <CardHeader className="pb-3">
              <CardDescription>Favorite Courses</CardDescription>
              <div className="flex items-baseline gap-2">
                <CardTitle className="text-3xl">{favoriteCourses.length}</CardTitle>
                <Heart className={`w-5 h-5 ${showOnlyFavorites ? 'fill-[#2B8B3B]' : ''} ${brandStyles.text}`} />
              </div>
            </CardHeader>
          </Card>

          <Card 
            className="border-2 border-gray-100 cursor-pointer hover:border-[#2B8B3B] transition-all"
            onClick={() => handleNavigate('bookings')}
          >
            <CardHeader className="pb-3">
              <CardDescription>Total Rounds Played</CardDescription>
              <div className="flex items-baseline gap-2">
                <CardTitle className="text-3xl">{loadingBookings ? '-' : pastBookings.filter(b => b.status === 'completed').length}</CardTitle>
                <TrendingUp className={`w-5 h-5 ${brandStyles.text}`} />
              </div>
            </CardHeader>
          </Card>

          <Card 
            className="border-2 border-gray-100 cursor-pointer hover:border-[#2B8B3B] transition-all"
            onClick={() => handleNavigate('bookings', true)}
          >
            <CardHeader className="pb-3">
              <CardDescription><span className="font-bold text-[#2B8B3B]">discgo</span> Distro Fund</CardDescription>
              <div className="flex items-baseline gap-2">
                <CardTitle className="text-3xl">{loadingBookings ? '-' : `$${[...upcomingBookings, ...pastBookings].filter(b => !b.refundedAt && b.status !== 'refunded' && new Date(b.slotStart).getFullYear() === new Date().getFullYear()).reduce((sum, b) => sum + b.distroFundContribution, 0).toFixed(2)}`}</CardTitle>
                <img src={basketIcon} alt="Basket" className="w-5 h-5" style={{ filter: 'brightness(0) saturate(100%) invert(37%) sepia(32%) saturate(1288%) hue-rotate(90deg) brightness(95%) contrast(85%)' }} />
              </div>
            </CardHeader>
          </Card>
        </div>
        </div>
      </div>

      {/* Bottom Section - White Background */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Find Courses Section */}
        <div ref={coursesRef} id="find-courses" className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className={brandStyles.text}>
              {showOnlyFavorites ? 'Your Favorite Courses' : 'Find Courses'}
            </h3>
            {showOnlyFavorites && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowOnlyFavorites(false)}
                className="rounded-lg"
              >
                <X className="w-4 h-4 mr-2" />
                Show All Courses
              </Button>
            )}
          </div>

          {/* Search and Filters */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search by course or location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 py-6 bg-white border-2 border-gray-200 rounded-lg"
                />
              </div>
              <Button
                variant="outline"
                onClick={() => setViewMode(viewMode === 'list' ? 'map' : 'list')}
                className={`border-2 ${viewMode === 'map' ? 'bg-[#2B8B3B] text-white border-[#2B8B3B]' : 'border-gray-200'} rounded-lg px-6 py-6`}
              >
                {viewMode === 'list' ? (
                  <>
                    <MapIcon className="w-5 h-5 mr-2" />
                    Map View
                  </>
                ) : (
                  <>
                    <List className="w-5 h-5 mr-2" />
                    List View
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className={`border-2 ${showFilters ? 'bg-[#2B8B3B] text-white border-[#2B8B3B]' : 'border-gray-200'} rounded-lg px-6 py-6`}
              >
                <Filter className="w-5 h-5 mr-2" />
                Filters {activeFilters.length > 0 && `(${activeFilters.length})`}
              </Button>
            </div>

            {/* Filter Chips */}
            {showFilters && (
              <div className="flex flex-wrap gap-2 p-4 bg-white rounded-lg border-2 border-gray-200">
                <Button
                  variant={activeFilters.includes('near-me') ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => toggleFilter('near-me')}
                  className={`rounded-full ${activeFilters.includes('near-me') ? brandStyles.primary : ''}`}
                >
                  <MapPinIcon className="w-4 h-4 mr-1" />
                  Near Me
                  {activeFilters.includes('near-me') && <X className="w-3 h-3 ml-1" />}
                </Button>
                <Button
                  variant={activeFilters.includes('under-20') ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => toggleFilter('under-20')}
                  className={`rounded-full ${activeFilters.includes('under-20') ? brandStyles.primary : ''}`}
                >
                  <DollarSign className="w-4 h-4 mr-1" />
                  Under $20
                  {activeFilters.includes('under-20') && <X className="w-3 h-3 ml-1" />}
                </Button>
                <Button
                  variant={activeFilters.includes('high-rated') ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => toggleFilter('high-rated')}
                  className={`rounded-full ${activeFilters.includes('high-rated') ? brandStyles.primary : ''}`}
                >
                  <Star className="w-4 h-4 mr-1" />
                  4.5+ Rating
                  {activeFilters.includes('high-rated') && <X className="w-3 h-3 ml-1" />}
                </Button>
                <Button
                  variant={activeFilters.includes('available-today') ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => toggleFilter('available-today')}
                  className={`rounded-full ${activeFilters.includes('available-today') ? brandStyles.primary : ''}`}
                >
                  <Clock className="w-4 h-4 mr-1" />
                  Available Today
                  {activeFilters.includes('available-today') && <X className="w-3 h-3 ml-1" />}
                </Button>
                <Button
                  variant={activeFilters.includes('dog-friendly') ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => toggleFilter('dog-friendly')}
                  className={`rounded-full ${activeFilters.includes('dog-friendly') ? brandStyles.primary : ''}`}
                >
                  <PawPrint className="w-4 h-4 mr-1" />
                  Dog Friendly
                  {activeFilters.includes('dog-friendly') && <X className="w-3 h-3 ml-1" />}
                </Button>
              </div>
            )}
          </div>

          {/* List or Map View */}
          {viewMode === 'list' ? (
            // Course Cards Grid
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredCourses.map(course => (
                <CourseCard key={course.id} course={course} />
              ))}
            </div>
          ) : (
            // Map View with Side Panel
            <div className="flex gap-6 h-[600px]">
              {/* Course List Panel */}
              <div className="w-96 flex-shrink-0 overflow-y-auto space-y-4 pr-2">
                {filteredCourses.map(course => (
                  <CourseCard key={course.id} course={course} />
                ))}
              </div>
              
              {/* Real Interactive Map */}
              <div className="flex-1 flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <MapIcon className="w-5 h-5 text-[#2B8B3B]" />
                    <h4 className={brandStyles.text}>Courses in {displayCity}</h4>
                  </div>
                  <Badge className={brandStyles.primary}>
                    {filteredCourses.length} {filteredCourses.length === 1 ? 'course' : 'courses'}
                  </Badge>
                </div>
                <div className="flex-1">
                  <MapView 
                    courses={filteredCourses} 
                    onCourseClick={openBookingFlow}
                  />
                </div>
              </div>
            </div>
          )}

          {filteredCourses.length === 0 && (
            <div className="text-center py-12">
              <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No courses found matching your criteria</p>
              <Button 
                variant="outline" 
                onClick={() => { setSearchTerm(''); setActiveFilters([]); setShowOnlyFavorites(false); }}
                className="mt-4"
              >
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </main>

      {/* Booking Requests Section - Beige Background */}
      <div className="bg-[#F5F5DC] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={bookingsRef} className="mb-8">
            <h3 className={`mb-6 ${brandStyles.text}`}>Upcoming Tee Times</h3>

            <div className="space-y-4">
            {loadingBookings ? (
              <div className="text-center py-8 text-gray-600">Loading bookings...</div>
            ) : upcomingBookings.length === 0 ? (
              <div className="text-center py-8 text-gray-600">No upcoming tee times</div>
            ) : upcomingBookings.map(booking => (
              <div key={booking.id} className="flex flex-col sm:flex-row border-2 border-gray-100 rounded-lg overflow-hidden bg-white">
                {/* Course Image */}
                <div className="h-48 sm:w-32 sm:h-32 bg-gray-200 flex-shrink-0">
                  <ImageWithFallback
                    src={booking.courseImage}
                    alt={booking.courseName}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Booking Details */}
                <div className="flex-1 p-3">
                  <div className="flex items-start justify-between mb-1.5">
                    <h4 className="mb-0.5">{booking.courseName}</h4>
                    {booking.status === 'requested' ? (
                      <Badge variant="outline" className="border-orange-500 text-orange-600 bg-orange-50 ml-2">
                        Pending Request
                      </Badge>
                    ) : (
                      <Badge className={`${brandStyles.primary} ml-2`}>
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Confirmed
                      </Badge>
                    )}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm text-gray-600 mb-3">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(booking.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>{booking.time}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      <span>{booking.players} players</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4" />
                      <span className="font-medium text-gray-900">${booking.totalPrice}</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" className="rounded-lg">
                      View Details
                    </Button>
                    {booking.status === 'confirmed' && (
                      <Button variant="outline" size="sm" className="rounded-lg">
                        <Calendar className="w-4 h-4 mr-1" />
                        Add to Calendar
                      </Button>
                    )}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg"
                      onClick={() => openCancelDialog(booking)}
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-6 text-sm text-gray-600">
              <a href="#" className="hover:text-[#2B8B3B] transition-colors">About</a>
              <a href="#" className="hover:text-[#2B8B3B] transition-colors">Support</a>
              <a href="#" className="hover:text-[#2B8B3B] transition-colors">Terms</a>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">made with ❤️ for the disc golf community</span>
            </div>
          </div>
        </div>
      </footer>

      {/* Booking Flow Dialog */}
      <Dialog open={bookingDialogOpen} onOpenChange={setBookingDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <Sentry.ErrorBoundary fallback={
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
              <p className="text-base font-medium text-gray-900">Something went wrong in the booking flow.</p>
              <p className="text-sm text-gray-500">Please close this dialog and try again.</p>
              <button
                onClick={() => setBookingDialogOpen(false)}
                className="mt-2 px-4 py-2 text-sm font-medium rounded-md bg-gray-900 text-white hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          }>
          {!bookingConfirmed ? (
            <>
              <DialogHeader className="mt-3">
                <DialogTitle className={brandStyles.text}>
                  {bookingStep === 1 && 'Select Date & Time'}
                  {bookingStep === 2 && 'Player Information'}
                  {bookingStep === 3 && 'Payment & Confirmation'}
                </DialogTitle>
                <DialogDescription>
                  {selectedCourse?.name}
                </DialogDescription>
              </DialogHeader>

              {/* Progress Indicator */}
              <div className="flex items-center gap-2 mb-6">
                {[1, 2, 3].map((step) => (
                  <React.Fragment key={step}>
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full ${bookingStep >= step ? brandStyles.primary : 'bg-gray-200 text-gray-600'}`}>
                      {bookingStep > step ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <span>{step}</span>
                      )}
                    </div>
                    {step < 3 && <div className={`flex-1 h-1 ${bookingStep > step ? 'bg-[#2B8B3B]' : 'bg-gray-200'}`} />}
                  </React.Fragment>
                ))}
              </div>

              {/* Step 1: Date & Time Selection */}
              {bookingStep === 1 && selectedCourse && (
                <div className="space-y-6">
                  {/* Course Info with Image Carousel */}
                  <Card className="border-2 border-gray-100">
                    <CardContent className="p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Image Carousel */}
                        <div>
                          <Carousel className="w-full">
                            <CarouselContent>
                              {selectedCourse.images.map((image, index) => (
                                <CarouselItem key={index}>
                                  <ImageWithFallback
                                    src={image}
                                    alt={`${selectedCourse.name} - Image ${index + 1}`}
                                    className="w-full h-64 rounded-lg object-cover"
                                  />
                                </CarouselItem>
                              ))}
                            </CarouselContent>
                            <CarouselPrevious className="left-2" />
                            <CarouselNext className="right-2" />
                          </Carousel>
                        </div>

                        {/* Course Details */}
                        <div className="flex flex-col">
                          <h4 className="mb-2">{selectedCourse.name}</h4>
                          <div className="flex items-center gap-2 mb-3">
                            <div className="flex items-center gap-1">
                              <Star className={`w-5 h-5 ${selectedCourse.reviews > 0 ? 'fill-[#2B8B3B]' : 'fill-gray-300'} ${selectedCourse.reviews > 0 ? brandStyles.text : 'text-gray-300'}`} />
                              {selectedCourse.reviews > 0 ? (
                                <>
                                  <span className="font-medium text-lg">{selectedCourse.rating}</span>
                                  <span className="text-sm text-gray-500">({selectedCourse.reviews} reviews)</span>
                                </>
                              ) : (
                                <span className="text-sm text-gray-400">No reviews yet</span>
                              )}
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 mb-3">{selectedCourse.description}</p>
                          <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                            <MapPin className="w-4 h-4" />
                            <span>{selectedCourse.location}</span>
                            <Separator orientation="vertical" className="h-4" />
                            <span>{selectedCourse.holes} holes</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <span className={`${brandStyles.text} font-medium text-lg`}>${selectedCourse.pricePerPlayer}/player</span>
                          </div>
                          {selectedCourse.amenities.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-3">
                              {selectedCourse.amenities.map((amenity) => (
                                <Badge key={amenity} variant="outline" className="border-[#2B8B3B] text-[#2B8B3B]">
                                  {amenity}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Review Breakdown */}
                  <Card className="border-2 border-gray-100">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">What players are saying</CardTitle>
                      <CardDescription>Based on {selectedCourse.reviews} verified reviews</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Star Rating Distribution */}
                      <div className="space-y-2">
                        {[5, 4, 3, 2, 1].map((stars) => {
                          const count = selectedCourse.reviewBreakdown[stars as keyof typeof selectedCourse.reviewBreakdown];
                          const percentage = (count / selectedCourse.reviews) * 100;
                          return (
                            <div key={stars} className="flex items-center gap-3">
                              <div className="flex items-center gap-1 w-16">
                                <span className="text-sm font-medium">{stars}</span>
                                <Star className="w-3 h-3 fill-[#2B8B3B] text-[#2B8B3B]" />
                              </div>
                              <Progress value={percentage} className="flex-1 h-2" />
                              <span className="text-sm text-gray-600 w-12 text-right">{count}</span>
                            </div>
                          );
                        })}
                      </div>

                      <Separator />

                      {/* Top Aspects */}
                      <div>
                        <h5 className="text-sm font-medium mb-2">What players enjoyed</h5>
                        <div className="flex flex-wrap gap-2 mb-4">
                          {selectedCourse.topAspects.positive.map((aspect, index) => (
                            <Badge key={index} className="bg-[#2B8B3B]/10 text-[#2B8B3B] border-[#2B8B3B] hover:bg-[#2B8B3B]/20">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              {aspect}
                            </Badge>
                          ))}
                        </div>
                        {selectedCourse.topAspects.negative.length > 0 && (
                          <>
                            <h5 className="text-sm font-medium mb-2">Things to note</h5>
                            <div className="flex flex-wrap gap-2">
                              {selectedCourse.topAspects.negative.map((aspect, index) => (
                                <Badge key={index} variant="outline" className="border-orange-500 text-orange-600 bg-orange-50">
                                  <Info className="w-3 h-3 mr-1" />
                                  {aspect}
                                </Badge>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Date Selection */}
                  <div>
                    <Label htmlFor="date">Select Date</Label>
                    <Input
                      id="date"
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="mt-2"
                    />
                  </div>

                  {/* Time Selection */}
                  <div>
                    <Label>Select Tee Time</Label>
                    {loadingTeeTimes ? (
                      <div className="text-center py-8 text-gray-500">Loading available times...</div>
                    ) : availableTeeTimes.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">No tee times available for this date</div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mt-2">
                        {availableTeeTimes.map((slot) => {
                          const isSelected = selectedConfigId === slot.configId && selectedSlotStart === slot.startTime;
                          const priceDollars = (slot.priceCents / 100).toFixed(2);

                          return (
                            <Button
                              key={`${slot.configId}-${slot.startTime}`}
                              variant={isSelected ? 'default' : 'outline'}
                              disabled={slot.isBooked}
                              onClick={() => {
                                if (slot.isBooked) return;
                                setSelectedConfigId(slot.configId);
                                setSelectedSlotStart(slot.startTime);
                                setSelectedTime(slot.displayTime);
                                setSelectedPriceCents(slot.priceCents);
                                setSelectedBookingType(slot.bookingType);
                              }}
                              className={`flex flex-col h-auto py-2 ${isSelected ? brandStyles.primary : ''} ${slot.isBooked ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              <span>{slot.displayTime}</span>
                              <span className={`text-xs mt-0.5 ${slot.isBooked ? 'text-red-500' : 'text-green-600'}`}>
                                {slot.isBooked ? 'Booked' : `Up to ${slot.maxPlayers} players`}
                              </span>
                              {!slot.isBooked && (
                                <span className="text-xs mt-0.5 text-gray-600">
                                  {slot.pricingType === 'per_player'
                                    ? `$${priceDollars}/player`
                                    : `$${priceDollars} total`}
                                </span>
                              )}
                              {!slot.isBooked && slot.bookingType === 'request' && (
                                <span className="text-xs mt-0.5 text-blue-600">Request</span>
                              )}
                            </Button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                </div>
              )}

              {/* Step 2: Player Information */}
              {bookingStep === 2 && selectedCourse && (
                <div className="space-y-6">
                  {/* Player Count */}
                  <div>
                    <Label htmlFor="players">Number of Players</Label>
                    {(() => {
                      const selectedSlot = availableTeeTimes.find(
                        slot => slot.configId === selectedConfigId && slot.startTime === selectedSlotStart
                      );
                      const maxAllowed = selectedSlot?.maxPlayers || 4;

                      return (
                        <>
                          <div className="flex items-center gap-4 mt-2">
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => setPlayerCount(Math.max(1, playerCount - 1))}
                            >
                              -
                            </Button>
                            <span className="text-2xl font-medium w-12 text-center">{playerCount}</span>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => setPlayerCount(Math.min(maxAllowed, playerCount + 1))}
                              disabled={playerCount >= maxAllowed}
                            >
                              +
                            </Button>
                          </div>
                          <p className="text-sm text-gray-500 mt-2">
                            Max {maxAllowed} {maxAllowed === 1 ? 'player' : 'players'} per tee time
                          </p>
                        </>
                      );
                    })()}
                  </div>

                  {/* Contact Information */}
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="name">Full Name</Label>
                      <Input
                        id="name"
                        type="text"
                        value={playerName}
                        onChange={(e) => setPlayerName(e.target.value)}
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email">Email Address</Label>
                      <Input
                        id="email"
                        type="email"
                        value={playerEmail}
                        onChange={(e) => setPlayerEmail(e.target.value)}
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={playerPhone}
                        onChange={(e) => setPlayerPhone(e.target.value)}
                        placeholder="(555) 123-4567"
                        className="mt-2"
                      />
                    </div>
                  </div>

                  {/* Booking Summary */}
                  <Card className={`${brandStyles.accent} border-2 border-[#2B8B3B]`}>
                    <CardContent className="p-4">
                      <h4 className="mb-3">Booking Summary</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Course:</span>
                          <span className="font-medium">{selectedCourse.name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Date & Time:</span>
                          <span className="font-medium">{(() => { const [y,m,d] = selectedDate.split('-'); return new Date(Number(y), Number(m)-1, Number(d)).toLocaleDateString(); })()} at {selectedTime}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Players:</span>
                          <span className="font-medium">{playerCount}</span>
                        </div>
                        <Separator className="my-2" />
                        <div className="flex justify-between">
                          <span className="text-gray-600">Price per player:</span>
                          <span className="font-medium">${selectedCourse.pricePerPlayer}</span>
                        </div>
                        <div className="flex justify-between text-base">
                          <span className={`font-medium ${brandStyles.text}`}>Subtotal:</span>
                          <span className={`font-medium ${brandStyles.text}`}>${calculateTotal()}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Step 3: Payment */}
              {bookingStep === 3 && selectedCourse && (
                <div className="space-y-6">
                  {/* Booking Summary */}
                  <Card className="border-2 border-gray-100">
                    <CardContent className="p-4">
                      <h4 className="mb-3">Booking Details</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Course:</span>
                          <span className="font-medium">{selectedCourse.name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Date & Time:</span>
                          <span className="font-medium">{(() => { const [y,m,d] = selectedDate.split('-'); return new Date(Number(y), Number(m)-1, Number(d)).toLocaleDateString(); })()} at {selectedTime}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Players:</span>
                          <span className="font-medium">{playerCount}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Contact:</span>
                          <span className="font-medium">{playerEmail}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Price Breakdown — shown for auto bookings using server-calculated amounts */}
                  {selectedBookingType === 'auto' && paymentAmounts && (
                    <Card className={`${brandStyles.accent} border-2 border-[#2B8B3B]`}>
                      <CardContent className="p-4">
                        <h4 className="mb-3">Price Breakdown</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Course Fee:</span>
                            <span className="font-medium">${(paymentAmounts.courseAmountCents / 100).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Booking Fee:</span>
                            <span className="font-medium">${(paymentAmounts.bookingFeeCents / 100).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Processing Fee:</span>
                            <span className="font-medium">${(paymentAmounts.stripeFeeCents / 100).toFixed(2)}</span>
                          </div>
                          <Separator className="my-2" />
                          <div className="flex justify-between text-lg">
                            <span className={`font-medium ${brandStyles.text}`}>Total:</span>
                            <span className={`font-medium ${brandStyles.text}`}>${(paymentAmounts.totalCents / 100).toFixed(2)}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Refund Policy Acknowledgment */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                    <div className="flex items-start gap-2">
                      <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-blue-900">Cancellation Policy</p>
                        <p className="text-blue-700 mt-1">
                          {(() => {
                            const policy = paymentAmounts?.refundPolicyName || selectedCourse.refundPolicyDescription || 'Flexible';
                            if (policy === 'Flexible') return 'Full refund available any time before your tee time.';
                            if (policy === 'Standard') return 'Full refund available if cancelled 24+ hours before your tee time.';
                            if (policy === 'Strict') return 'Full refund available if cancelled 48+ hours before your tee time.';
                            return selectedCourse.refundPolicyDescription || 'No refund policy set for this course.';
                          })()}
                        </p>
                        <p className="text-blue-600 mt-1 text-xs">No-shows receive no refund under any policy.</p>
                      </div>
                    </div>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={policyAgreed}
                        onChange={e => setPolicyAgreed(e.target.checked)}
                        className="mt-0.5 w-4 h-4 accent-[#2B8B3B] flex-shrink-0"
                      />
                      <span className="text-sm text-blue-900">I understand and agree to the cancellation policy for this booking.</span>
                    </label>
                  </div>

                  {/* Stripe Payment Element (auto and request bookings) */}
                  {(
                    <div>
                      {paymentIntentLoading && (
                        <div className="text-sm text-gray-500 text-center py-4">Setting up secure payment…</div>
                      )}
                      {paymentIntentError && (
                        <div className="flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
                          <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          {paymentIntentError}
                        </div>
                      )}
                      {clientSecret && !paymentIntentLoading && (
                        <StripeCheckout
                          clientSecret={clientSecret}
                          returnUrl={`${window.location.origin}${window.location.pathname}`}
                          totalCents={paymentAmounts?.totalCents ?? 0}
                          policyAgreed={policyAgreed}
                          bookingType="auto"
                          savedCard={savedCard}
                          usingSavedCard={usingSavedCard}
                          saveCard={saveCard}
                          onSaveCardChange={handleSaveCardChange}
                          onUseDifferentCard={handleUseDifferentCard}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between pt-4 border-t">
                {bookingStep > 1 && (
                  <Button variant="outline" onClick={handlePreviousStep}>
                    <ChevronLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                )}
                {/* Step 3: StripeCheckout renders its own Pay button for both booking types */}
                {bookingStep !== 3 && (
                  <Button
                    className={`${brandStyles.primary} ml-auto`}
                    onClick={handleNextStep}
                    disabled={!canProceedToNextStep()}
                  >
                    Continue
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </>
          ) : (
            // Confirmation Screen
            (() => {
              // Use post-redirect display data when available (state is cleared after Stripe redirect)
              const d = confirmedBookingDisplay;
              const isRequest = d ? !!d.isRequest : selectedBookingType === 'request';
              const confNum = d?.confirmationNumber ?? confirmationNumber;
              const courseName = d?.courseName ?? selectedCourse?.name ?? '';
              const dateDisplay = d
                ? (d.dateStr ? (() => { const [y,m,d2] = d.dateStr.split('-'); return new Date(Number(y), Number(m)-1, Number(d2)).toLocaleDateString(); })() : '')
                : (() => { const [y,m,dd] = selectedDate.split('-'); return new Date(Number(y), Number(m)-1, Number(dd)).toLocaleDateString(); })();
              const timeDisplay = d?.timeStr ?? selectedTime;
              const playersDisplay = d?.playerCount ?? playerCount;
              const emailDisplay = d?.playerEmail ?? playerEmail;
              const totalDisplay = d?.totalPaid ?? `$${calculateTotalWithFees()}`;
              const refundDisplay = d?.refundPolicyName
                ? (() => {
                    if (d.refundPolicyName === 'Flexible') return 'Full refund available any time before your tee time.';
                    if (d.refundPolicyName === 'Standard') return 'Full refund available if cancelled 24+ hours before your tee time.';
                    if (d.refundPolicyName === 'Strict') return 'Full refund available if cancelled 48+ hours before your tee time.';
                    return d.refundPolicyName;
                  })()
                : null;
              return (
                <div className="text-center">
                  <h3 className={`mb-2 ${isRequest ? 'text-[#2563eb]' : brandStyles.text}`}>
                    {isRequest ? 'Request Submitted!' : 'Booking Confirmed!'}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {isRequest
                      ? 'Your booking request has been sent to the course owner'
                      : 'Your tee time has been successfully booked'}
                  </p>

                  <Card className={`${brandStyles.accent} border-2 ${isRequest ? 'border-[#2563eb]' : 'border-[#2B8B3B]'} mb-6`}>
                    <CardContent className="p-6">
                      <div className="space-y-3">
                        <div>
                          <span className="text-sm text-gray-600">
                            {isRequest ? 'Request Number' : 'Confirmation Number'}
                          </span>
                          <p className="text-2xl font-medium mt-1">{confNum}</p>
                        </div>
                        <Separator />
                        <div className="text-left space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Course:</span>
                            <span className="font-medium">{courseName}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Date & Time:</span>
                            <span className="font-medium">{dateDisplay} at {timeDisplay}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Players:</span>
                            <span className="font-medium">{playersDisplay}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">
                              {isRequest ? 'Estimated Total:' : 'Total Paid:'}
                            </span>
                            <span className="font-medium">{totalDisplay}</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {refundDisplay && !isRequest && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
                      <p className="text-sm text-blue-900 font-medium mb-1">Refund Policy</p>
                      <p className="text-sm text-blue-700">{refundDisplay}</p>
                    </div>
                  )}

                  {isRequest ? (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                      <p className="text-sm text-blue-900 font-medium mb-1">Pending Approval</p>
                      <p className="text-sm text-blue-700">
                        You'll receive an email notification at {emailDisplay} when your request is approved by the course owner.
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-600 mb-6">
                      A confirmation email has been sent to {emailDisplay}
                    </p>
                  )}

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  variant="outline"
                  onClick={closeBookingDialog}
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Add to Calendar
                </Button>
                {isRequest ? (
                  <button
                    onClick={closeBookingDialog}
                    style={{
                      backgroundColor: '#2563eb',
                      color: 'white',
                      padding: '0.5rem 1rem',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1d4ed8'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#2563eb'; }}
                  >
                    Done
                  </button>
                ) : (
                  <Button className={brandStyles.primary} onClick={closeBookingDialog}>
                    Done
                  </Button>
                )}
              </div>
            </div>
              );
            })()
          )}
          </Sentry.ErrorBoundary>
        </DialogContent>
      </Dialog>

      {/* Cancellation Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={open => { if (!cancelLoading) setCancelDialogOpen(open); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cancel Booking</DialogTitle>
            <DialogDescription>
              {bookingToCancel?.courseName} — {bookingToCancel?.date} at {bookingToCancel?.time}
            </DialogDescription>
          </DialogHeader>
          {bookingToCancel && (
            <div className="space-y-4 mt-2">
              {isRefundEligible(bookingToCancel) ? (
                <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-green-800">You are eligible for a full refund.</p>
                    <p className="text-green-700 mt-0.5">Your payment will be refunded to your original payment method.</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm">
                  <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-800">This booking is outside the refund window.</p>
                    <p className="text-red-700 mt-0.5">No refund will be issued for this cancellation.</p>
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setCancelDialogOpen(false)} disabled={cancelLoading}>
                  Keep Booking
                </Button>
                <Button
                  style={{ backgroundColor: '#dc2626', color: '#fff', border: 'none' }}
                  onClick={confirmCancelBooking}
                  disabled={cancelLoading}
                >
                  {cancelLoading ? 'Cancelling…' : 'Confirm Cancellation'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
